import { layout, overlaps, spanOn, toClock, toMinutes, weekdayOf } from "../../services/datetime";

/**
 * The pieces every diary view shares: how tall an hour is, what colour a status
 * wears, and how the API's layers become positioned blocks.
 *
 * Kept out of the views so the day, week and resource grids cannot drift from
 * each other — three copies of "minutes to pixels" would eventually disagree.
 */

/** Pixels per hour. The grid's one magic number, in one place. */
export const HOUR_HEIGHT = 56;

/** Minutes -> pixels from the top of the grid. */
export const offsetFor = (minutes, dayStart) => ((minutes - dayStart) / 60) * HOUR_HEIGHT;

/** A duration in minutes -> a height, never smaller than something readable. */
export const heightFor = (minutes) => Math.max(18, (minutes / 60) * HOUR_HEIGHT);

/**
 * Appointment statuses, in the order a visit lives them. The filter chips, the
 * legend and the counts all read this, so a status added here appears in all
 * three at once.
 */
export const STATUS_ORDER = [
  "pending",
  "confirmed",
  "checked_in",
  "in_service",
  "completed",
  "cancelled",
  "no_show",
];

/**
 * Statuses that have let their slot go. The server says the same thing with
 * `Appointment::BLOCKING_STATUSES` — these are the two it leaves out — and the
 * grid draws them out of the active bookings' way rather than on top of them.
 * (Completed also no longer blocks, but it is the record of work done in that
 * slot and keeps its place.)
 */
export const INACTIVE_STATUSES = ["cancelled", "no_show"];

export const isInactive = (status) => INACTIVE_STATUSES.includes(status);

/*
 * One hue per status, from the `st-*` tokens in index.scss so both themes
 * flip together: pending amber, confirmed blue, checked-in teal, in-service
 * purple, completed green, cancelled grey, no-show red.
 *
 * Every class string is written out in full — Tailwind only generates the
 * utilities it can find as literal text, so a template like `bg-st-${x}` would
 * silently produce nothing.
 */

/** A rounded status pill: badges in the detail page, timeline and agenda. */
export const STATUS_STYLES = {
  pending: "bg-st-pending-wash border-st-pending text-st-pending-text",
  confirmed: "bg-st-confirmed-wash border-st-confirmed text-st-confirmed-text",
  checked_in: "bg-st-checked-in-wash border-st-checked-in text-st-checked-in-text",
  in_service: "bg-st-in-service-wash border-st-in-service text-st-in-service-text",
  completed: "bg-st-completed-wash border-st-completed text-st-completed-text",
  cancelled: "bg-st-cancelled-wash border-st-cancelled text-st-cancelled-text",
  no_show: "bg-st-no-show-wash border-st-no-show text-st-no-show-text",
};

export const statusStyle = (status) => STATUS_STYLES[status] ?? STATUS_STYLES.confirmed;

/** The solid swatch: legend keys, chip dots, the badge inside a tight block. */
export const STATUS_DOTS = {
  pending: "bg-st-pending",
  confirmed: "bg-st-confirmed",
  checked_in: "bg-st-checked-in",
  in_service: "bg-st-in-service",
  completed: "bg-st-completed",
  cancelled: "bg-st-cancelled",
  no_show: "bg-st-no-show",
};

export const statusDot = (status) => STATUS_DOTS[status] ?? STATUS_DOTS.confirmed;

/**
 * A booking block on the grid or a row in the agenda: the status wash, a
 * thick border on the *start* edge (left in English, right in Arabic — the
 * logical `border-s` does that), and how loudly it should read.
 *
 * Cancelled and no-show are faded because they are history, not work;
 * completed is only slightly muted, because it is work that happened.
 */
const BLOCK_STYLES = {
  pending: "bg-st-pending-wash border-st-pending text-st-pending-text",
  confirmed: "bg-st-confirmed-wash border-st-confirmed text-st-confirmed-text",
  checked_in: "bg-st-checked-in-wash border-st-checked-in text-st-checked-in-text",
  in_service: "bg-st-in-service-wash border-st-in-service text-st-in-service-text",
  completed: "bg-st-completed-wash border-st-completed text-st-completed-text opacity-90",
  cancelled: "bg-st-cancelled-wash border-st-cancelled text-st-cancelled-text opacity-70",
  no_show: "bg-st-no-show-wash border-st-no-show text-st-no-show-text opacity-70",
};

export const blockStyle = (status) =>
  `border border-s-4 ${BLOCK_STYLES[status] ?? BLOCK_STYLES.confirmed}`;

/**
 * A list row (the agenda): only the start-edge colour and the fade — a full
 * wash on every row of a fortnight would drown the list.
 */
const ROW_STYLES = {
  pending: "border-s-st-pending",
  confirmed: "border-s-st-confirmed",
  checked_in: "border-s-st-checked-in",
  in_service: "border-s-st-in-service",
  completed: "border-s-st-completed opacity-90",
  cancelled: "border-s-st-cancelled opacity-70",
  no_show: "border-s-st-no-show opacity-70",
};

export const rowStyle = (status) => `border-s-4 ${ROW_STYLES[status] ?? ROW_STYLES.confirmed}`;

/** Cancelled reads as struck through — the booking that did not happen. */
export const titleStyle = (status) => (status === "cancelled" ? "line-through" : "");

/**
 * The window the grid should draw for a given date.
 *
 * Branch opening hours decide it, so a salon open 09:00–17:00 does not render
 * eight empty hours of night. Anything already booked outside those hours
 * widens the window rather than being clipped out of sight — an appointment the
 * diary cannot show is worse than an odd-looking grid.
 */
export const dayBounds = (dates, workingHours, appointments, blocks, timezone) => {
  let start = 24 * 60;
  let end = 0;

  dates.forEach((date) => {
    const weekday = weekdayOf(date);

    workingHours
      .filter((h) => h.day === weekday)
      .forEach((h) => {
        start = Math.min(start, toMinutes(h.opens_at));
        end = Math.max(end, toMinutes(h.closes_at));
      });
  });

  const widen = (fromIso, toIso) => {
    dates.forEach((date) => {
      const span = spanOn(fromIso, toIso, date, timezone);
      if (!span) return;
      start = Math.min(start, span.from);
      end = Math.max(end, span.to);
    });
  };

  appointments.forEach((a) => widen(a.starts_at, a.ends_at));
  blocks.forEach((b) => widen(b.starts_at, b.ends_at));

  // A branch with no hours at all still needs something to draw.
  if (start >= end) return { start: 9 * 60, end: 17 * 60 };

  // Whole hours, so the axis labels land on the lines.
  return { start: Math.floor(start / 60) * 60, end: Math.ceil(end / 60) * 60 };
};

/** The hour labels down the side of the grid. */
export const hourMarks = ({ start, end }) => {
  const marks = [];
  for (let m = start; m <= end; m += 60) marks.push({ minutes: m, label: toClock(m) });
  return marks;
};

/**
 * Turn one appointment into a positioned block for a given date, or null when
 * it does not touch that date.
 *
 * The *customer-facing* window is drawn — `starts_at`/`ends_at`, not
 * `blocks_from`/`blocks_to`. Buffers are real and the engine enforces them, but
 * showing them would tell a receptionist the appointment is longer than what
 * the customer was told.
 */
export const appointmentBlock = (appointment, date, timezone) => {
  const span = spanOn(appointment.starts_at, appointment.ends_at, date, timezone);
  if (!span) return null;

  return {
    kind: "appointment",
    id: appointment.id,
    span,
    appointment,
    // Which providers are on it — how the resource view picks its column.
    staffIds: (appointment.services ?? []).map((s) => s.staff_id).filter(Boolean),
  };
};

/**
 * Lay out one column's blocks so history never covers live work.
 *
 * Active bookings and blocked time are laid out among themselves exactly as
 * before, side by side where they overlap. Cancelled and no-show blocks are
 * then fitted around them:
 *
 * - one that overlaps nothing active keeps the full width — on an empty
 *   afternoon there is nothing to make room for;
 * - one that overlaps an active block goes into a narrow lane at the inline
 *   end (`GHOST_LANE` of the width), and every active block that touches it
 *   gives up that lane. Both stay visible and both stay clickable, and the
 *   active one keeps by far the larger share.
 *
 * Returns each block with `left` / `width` as percentages for
 * `insetInlineStart` / `width`, and `ghost: true` on the narrowed history.
 */
export const GHOST_LANE = 0.28;

export const layoutColumn = (items) => {
  const live = layout(items.filter((b) => !(b.kind === "appointment" && isInactive(b.appointment.status))));
  const history = items.filter((b) => b.kind === "appointment" && isInactive(b.appointment.status));

  const crowded = history.filter((h) => live.some((b) => overlaps(b.span, h.span)));
  const free = history.filter((h) => !crowded.includes(h));

  // An active block makes room when it, or anything sharing its row, meets a
  // crowded history block — so a cluster narrows together and never overlaps.
  const narrows = (block) =>
    crowded.some(
      (h) =>
        overlaps(block.span, h.span) ||
        live.some((other) => overlaps(other.span, block.span) && overlaps(other.span, h.span)),
    );

  const placed = live.map((block) => {
    const share = narrows(block) ? 1 - GHOST_LANE : 1;
    return {
      ...block,
      left: ((block.column * share) / block.columns) * 100,
      width: (share / block.columns) * 100,
    };
  });

  layout(crowded).forEach((block) => {
    placed.push({
      ...block,
      ghost: true,
      left: (1 - GHOST_LANE + (block.column * GHOST_LANE) / block.columns) * 100,
      width: (GHOST_LANE / block.columns) * 100,
    });
  });

  layout(free).forEach((block) => {
    placed.push({
      ...block,
      left: (block.column / block.columns) * 100,
      width: 100 / block.columns,
    });
  });

  return placed;
};

/** The same for a block of time taken out of the diary. */
export const blockedBlock = (blocked, date, timezone) => {
  const span = spanOn(blocked.starts_at, blocked.ends_at, date, timezone);
  if (!span) return null;

  return {
    kind: "blocked",
    id: blocked.id,
    span,
    blocked,
    staffIds: blocked.staff_id ? [blocked.staff_id] : [],
  };
};

/**
 * Whether a roster applies on a date — OUP-28.
 *
 * A branch may hold several: a summer week and a winter one, or an always-on
 * week plus a temporary cover. A null at either end is unbounded, not missing,
 * so a row with neither applies always — which is every row written before
 * periods existed. The API sends rosters that touch the range being drawn, and
 * a week view can span a changeover, so the choice is made per day here.
 */
const coversDate = (shift, date) =>
  (!shift.effective_from || date >= shift.effective_from) &&
  (!shift.effective_until || date <= shift.effective_until);

/**
 * The non-working background for one provider on one date: outside their
 * shift, in a break, or on leave.
 *
 * Returned as spans the view shades, rather than as a boolean per minute. Leave
 * that covers the whole day shades the whole column, which is the clearest way
 * to say "not in today" without hiding the column entirely.
 */
export const unavailableSpans = (provider, date, bounds) => {
  const weekday = weekdayOf(date);

  const allDayLeave = (provider.leave ?? []).some(
    (l) => l.is_all_day && l.starts_on <= date && l.ends_on >= date
  );

  if (allDayLeave) return [{ from: bounds.start, to: bounds.end, reason: "leave" }];

  const shifts = (provider.schedules ?? []).filter(
    (s) => s.day === weekday && coversDate(s, date)
  );

  if (shifts.length === 0) return [{ from: bounds.start, to: bounds.end, reason: "off" }];

  // Start with the whole day unavailable, then cut each shift out of it.
  let gaps = [{ from: bounds.start, to: bounds.end, reason: "off" }];

  shifts.forEach((shift) => {
    const from = toMinutes(shift.starts_at);
    const to = toMinutes(shift.ends_at);

    gaps = gaps.flatMap((gap) => {
      if (to <= gap.from || from >= gap.to) return [gap];

      const pieces = [];
      if (from > gap.from) pieces.push({ from: gap.from, to: from, reason: gap.reason });
      if (to < gap.to) pieces.push({ from: to, to: gap.to, reason: gap.reason });
      return pieces;
    });
  });

  // Breaks are unavailable inside a shift, so they are added back on top.
  shifts.forEach((shift) => {
    (shift.breaks ?? []).forEach((brk) => {
      gaps.push({
        from: toMinutes(brk.starts_at),
        to: toMinutes(brk.ends_at),
        reason: "break",
        label: brk.label,
      });
    });
  });

  // Part-day leave shades its own hours.
  (provider.leave ?? []).forEach((l) => {
    if (l.is_all_day || l.starts_on > date || l.ends_on < date) return;
    if (!l.starts_at || !l.ends_at) return;

    gaps.push({ from: toMinutes(l.starts_at), to: toMinutes(l.ends_at), reason: "leave" });
  });

  return gaps.filter((gap) => gap.to > gap.from);
};

/**
 * Which appointments are one customer's visit, and where each sits in it.
 *
 * Multi-service customer booking creates an independent appointment per
 * service — own status, own cancellation, own payment — and that is the
 * point. What is lost on a busy grid is that two of those rows are the same
 * person, here for one visit. `booking_group_id` is the server's record of
 * that, and this turns it into "1 of 2" for a label.
 *
 * **Display only.** It reads what the diary already loaded and couples
 * nothing: no extra request, no shared state, and no view acts on a group.
 * A booking the salon took has no group and never appears here, which is why
 * a lone appointment with a group id is skipped too — one is not a visit.
 *
 * @param  {Array<{id: string, booking_group_id?: string|null}>} appointments
 * @return {Map<string, {position: number, total: number}>}
 */
export const visitPositions = (appointments = []) => {
  const groups = new Map();

  appointments.forEach((appointment) => {
    const group = appointment?.booking_group_id;
    if (!group) return;

    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(appointment);
  });

  const positions = new Map();

  groups.forEach((members) => {
    if (members.length < 2) return;

    // In the order the customer will live them, not the order they arrived.
    const ordered = [...members].sort((a, b) =>
      String(a.local_starts_at ?? "").localeCompare(String(b.local_starts_at ?? "")),
    );

    ordered.forEach((appointment, index) => {
      positions.set(appointment.id, { position: index + 1, total: ordered.length });
    });
  });

  return positions;
};

/**
 * A customer booking for somebody else: the person in the chair, with who
 * booked it. `client` stays the customer (they pay and are messaged); `guest`
 * is the sitter. Short form for a diary block, full sentence elsewhere.
 */
export const clientLabel = (t, a) =>
  a?.guest?.name
    ? t("appointments.forGuestShort", { guest: a.guest.name, customer: a.client?.name ?? "—" })
    : a?.client?.name ?? t("diary.noClient");

/** "Booked by {customer} for {guest} ({phone})", or null for an own booking. */
export const bookedForLine = (t, a) =>
  a?.guest?.name
    ? t(a.guest.phone ? "appointments.bookedForWithPhone" : "appointments.bookedFor", {
        customer: a.client?.name ?? "—",
        guest: a.guest.name,
        phone: a.guest.phone ?? "",
      })
    : null;
